import createObjectSetters from '../../../utils/setters/createObjectSetters';
import DataSourceHelper from '../../helper/DataSourceHelper';
import DependencyFlowHelper from '../../helper/DependencyFlowHelper';
import { getTaps } from '../../pulse/runtime';
import type { GlobalStateStoreType } from '../../stores/GlobalStateStore';
import getDataSourceActions from './actions/dataSourceActions';
import type {
  CreateDataSourceControllerSliceProps,
  DataSourceControllerState,
  DataSourceMetaType,
  ManuallyTriggeredDataSourceType,
  QueryRequestResult,
} from './types';
import { getInitialDataSourceDependencyFlow } from './utils/getInitialDataSourceDependencyFlow';
import { getInitialManuallyTriggeredDataSources } from './utils/getInitialManuallyTriggeredDataSources';

function createDataSourceControllerSlice({
  getDataSourceRecordsStore,
  set,
  get,
  scopeId,
  capture,
}: CreateDataSourceControllerSliceProps) {
  const dataSources = getDataSourceRecordsStore().dataSources;
  const { setSingle: setDataSourceState, remove: rawRemoveDataSourceState } = createObjectSetters<
    GlobalStateStoreType,
    'dataSources',
    QueryRequestResult
  >('dataSources', set);

  // capture gates the tap per-store: getTaps() is global, so a RUNNER-mode tree in the builder
  // process would otherwise tap its dataSourceData writes and leak them into Pulse. See createTapBinder.
  const taps = capture ? getTaps() : undefined;
  const exposedSetDataSourceState =
    taps && scopeId
      ? taps.tapMapSetter(scopeId, 'dataSourceData', get, 'dataSources', setDataSourceState)
      : setDataSourceState;

  const removeDataSourceState =
    taps && scopeId
      ? taps.tapMapRemover(scopeId, 'dataSourceData', get, 'dataSources', rawRemoveDataSourceState)
      : rawRemoveDataSourceState;

  const {
    setSingle: setManualDataSourceState,
    remove: removeManualDataSourceState,
    removeMultiple: removeManualDataSourceStates,
  } = createObjectSetters<GlobalStateStoreType, 'manualDataSources', QueryRequestResult>(
    'manualDataSources',
    set,
  );

  const { setSingle: setPreviewTriggeredDataSource, remove: removePreviewTriggeredDataSource } =
    createObjectSetters<GlobalStateStoreType, 'previewTriggeredDataSources', boolean>(
      'previewTriggeredDataSources',
      set,
    );

  const { setSingle: setRunTriggeredDataSource, remove: removeRunTriggeredDataSource } =
    createObjectSetters<GlobalStateStoreType, 'runTriggeredDataSources', boolean>(
      'runTriggeredDataSources',
      set,
    );

  const {
    setSingle: setManuallyTriggeredDataSource,
    removeMultiple: removeManuallyTriggeredDataSources,
  } = createObjectSetters<
    GlobalStateStoreType,
    'manuallyTriggeredDataSources',
    ManuallyTriggeredDataSourceType
  >('manuallyTriggeredDataSources', set);

  const { setSingle: setDataSourceMeta, remove: removeDataSourceMeta } = createObjectSetters<
    GlobalStateStoreType,
    'dataSourcesMeta',
    DataSourceMetaType
  >('dataSourcesMeta', set);

  const setAggregationMetadataInDSMeta = (
    dataSourceId: string,
    data: QueryRequestResult | undefined,
  ) => {
    const currentMeta = get().dataSourcesMeta[dataSourceId];
    setDataSourceMeta(dataSourceId, { ...currentMeta, aggregationMetadata: data });
  };

  const setPreviewDataInDSMeta = (dataSourceId: string, data: QueryRequestResult | undefined) => {
    const currentMeta = get().dataSourcesMeta[dataSourceId];
    setDataSourceMeta(dataSourceId, { ...currentMeta, previewResult: data });
  };

  const dataSourceDependenciesGraph = dataSources
    ? DataSourceHelper.getDataSourceIntraDependencies(dataSources)
    : {};

  const initialDataSourceDependencyFlow = getInitialDataSourceDependencyFlow(dataSources);
  const completeDataSourceDependencies = DependencyFlowHelper.getDependencyFlowStatus({
    dependencyFlowInstance: {},
    entities: initialDataSourceDependencyFlow,
    intraDependencies: dataSourceDependenciesGraph,
  });

  const removeDataSourceFromGlobalState = (dataSourceId: string) => {
    removeDataSourceState(dataSourceId);
    removeDataSourceMeta(dataSourceId);
    removePreviewTriggeredDataSource(dataSourceId);
    removeRunTriggeredDataSource(dataSourceId);
    removeManuallyTriggeredDataSources([dataSourceId]);
  };

  const dataSourceActions = getDataSourceActions({
    get,
    set,
    setPreviewTriggeredDataSource,
    getDataSourceRecordsStore,
    setManuallyTriggeredDataSource,
    setRunTriggeredDataSource,
    removeDataSourceFromGlobalState,
  });

  const dataSourceControllerState: DataSourceControllerState = {
    // Create an empty object entry for each data source to maintain consistent key ordering
    // This is critical for our compiled Function evaluation system which uses new Function(keys, body)
    //
    // Example scenario:
    // If we have dataSources = { userAPI: {...}, ordersAPI: {...} }
    // We need dataSources state = { userAPI: undefined, ordersAPI: undefined }
    //
    // When evaluating expressions like "userAPI.data + ordersAPI.count", the compiled function becomes:
    // new Function('userAPI', 'ordersAPI', 'return userAPI.data + ordersAPI.count')
    //
    // The function parameters MUST match the exact order of Object.keys(context) and Object.values(context)
    // If keys = ['userAPI', 'ordersAPI'] but values = [ordersData, undefined] (wrong order),
    // the evaluation will fail because 'userAPI' parameter gets undefined instead of ordersData
    //
    // By pre-initializing all keys with undefined, we ensure:
    // 1. Consistent key ordering across all evaluations
    // 2. All required context keys are present when compiling functions
    // 3. No runtime errors from missing context properties
    dataSources: Object.fromEntries(Object.entries(dataSources).map(([id]) => [id, undefined])),
    manualDataSources: {},
    manuallyTriggeredDataSources: getInitialManuallyTriggeredDataSources(dataSources),
    dataSourcesMeta: {},
    previewTriggeredDataSources: {},
    runTriggeredDataSources: {},
    dataSourceDependencyFlow: completeDataSourceDependencies,
    dataSourceIntraDependencies: dataSourceDependenciesGraph,
  };

  return {
    dataSourceControllerState,
    dataSourceControllerActions: {
      ...dataSourceActions,
      setDataSourceState: exposedSetDataSourceState,
      setManualDataSourceState,
      removeManualDataSourceState,
      removeManualDataSourceStates,
      removeManuallyTriggeredDataSources,
      setPreviewDataInDSMeta,
      setAggregationMetadataInDSMeta,
    },
  };
}

export default createDataSourceControllerSlice;
export type UseCreateDataSourceControllerSliceProps = ReturnType<
  typeof createDataSourceControllerSlice
>;
